/**
 * Custom PDP Variant Swatches & Options Handler
 * Automatically enhances variant swatches using Style swatch images and Skin Color palettes
 */

(function () {
  'use strict';

  // Skin tones dictionary fallback
  const SKIN_TONES = {
    light: '#f7cfba',
    tan: '#e8af89',
    medium: '#d39268',
    'warm brown': '#b8754c',
    dark: '#7e4629',
    'deep dark': '#522b18',
    black: '#331a0e',
    brown: '#8d5524',
    white: '#fde0d9',
  };

  function isoToFlagEmoji(iso2) {
    if (!iso2 || iso2.length !== 2) return '';
    return iso2
      .toUpperCase()
      .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
  }

  /**
   * Parse Job Metafield data from JSON script tag or element data
   */
  function getJobMetafieldData(container) {
    const root = container || document;
    const scriptTag = root.querySelector('script[data-pfh-job-metafield], script[data-custom-job-metafield]');
    if (!scriptTag) return null;
    try {
      const raw = scriptTag.textContent.trim();
      if (!raw || raw === 'null' || raw === '""') return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') {
        return JSON.parse(parsed);
      }
      return parsed;
    } catch (e) {
      console.warn('[Custom PDP] Error parsing job metafield JSON:', e);
      return null;
    }
  }

  /**
   * Parse Style swatch images list from JSON script tag
   */
  function getStyleFilesData(container) {
    const root = container || document;
    const scriptTag = root.querySelector('script[data-pfh-style-files], script[data-custom-style-files]');
    if (!scriptTag) return [];
    try {
      const raw = scriptTag.textContent.trim();
      if (!raw || raw === 'null') return [];
      return JSON.parse(raw);
    } catch (e) {
      console.warn('[Custom PDP] Error parsing style files JSON:', e);
      return [];
    }
  }

  /**
   * Search for an image URL or color for a given option name and option value
   */
  function findSwatchFromMetafield(jobData, optionName, optionValue) {
    if (!jobData || !optionValue) return null;

    const optValNorm = optionValue.toString().trim().toLowerCase();
    const optNameNorm = (optionName || '').toString().trim().toLowerCase();

    // 1. Direct key match: jobData["Style 1"]
    for (const key of Object.keys(jobData)) {
      if (key.trim().toLowerCase() === optValNorm) {
        const item = jobData[key];
        if (typeof item === 'string') return item;
        if (item && item.image) return item.image;
        if (item && item.url) return item.url;
        if (item && item.color) return item.color;
      }
    }

    // 2. Nested under option name: jobData["Choose Style"]["Style 1"]
    for (const groupKey of Object.keys(jobData)) {
      const groupVal = jobData[groupKey];
      if (typeof groupVal === 'object' && groupVal !== null) {
        if (groupKey.toLowerCase().includes(optNameNorm) || optNameNorm.includes(groupKey.toLowerCase())) {
          for (const subKey of Object.keys(groupVal)) {
            if (subKey.trim().toLowerCase() === optValNorm) {
              const subItem = groupVal[subKey];
              if (typeof subItem === 'string') return subItem;
              if (subItem && subItem.image) return subItem.image;
              if (subItem && subItem.url) return subItem.url;
              if (subItem && subItem.color) return subItem.color;
            }
          }
        }

        if (Array.isArray(groupVal)) {
          for (const entry of groupVal) {
            const entryVal = entry.name || entry.value || entry.title || entry.label;
            if (entryVal && entryVal.toString().trim().toLowerCase() === optValNorm) {
              return entry.image || entry.url || entry.src || entry.color;
            }
          }
        }
      }
    }

    // 3. Array at top level: jobData = [{ value: "...", image: "..." }]
    if (Array.isArray(jobData)) {
      for (const entry of jobData) {
        const entryVal = entry.name || entry.value || entry.title || entry.label;
        if (entryVal && entryVal.toString().trim().toLowerCase() === optValNorm) {
          return entry.image || entry.url || entry.src || entry.color;
        }
      }
    }

    return null;
  }

  /**
   * Apply image/color swatches to all option labels
   */
  function enhanceSwatches(root = document) {
    const jobData = getJobMetafieldData(root);
    const styleFiles = getStyleFilesData(root);

    // Style Swatches
    const styleLabels = root.querySelectorAll('.custom-style-swatch-label, .pfh-style-swatch-label, [data-pfh-style-swatch]');
    styleLabels.forEach((label, idx) => {
      const optionName = label.dataset.optionName || 'Style';
      const optionValue = label.dataset.optionValue || label.getAttribute('title');
      if (!optionValue) return;

      let found = null;

      // 1. Check styleFiles list (from Style swatch images metafield)
      if (styleFiles && styleFiles.length > 0) {
        const cleanVal = optionValue.toLowerCase().replace(/[\s_\-\.']/g, '');
        const matched = styleFiles.find((f) => {
          if (!f || !f.url) return false;
          const cleanUrl = f.url.toLowerCase().replace(/[\s_\-\.']/g, '');
          const cleanAlt = (f.alt || '').toLowerCase().replace(/[\s_\-\.']/g, '');
          return cleanUrl.includes(cleanVal) || (cleanAlt && cleanAlt.includes(cleanVal));
        });

        if (matched) {
          found = matched.url;
        } else {
          // Fallback matching by position/index
          const optIdx = parseInt(label.dataset.optionIndex, 10);
          const targetIdx = !isNaN(optIdx) ? optIdx : idx;
          if (styleFiles[targetIdx] && styleFiles[targetIdx].url) {
            found = styleFiles[targetIdx].url;
          }
        }
      }

      // 2. Check Job Metafield
      if (!found) {
        found = findSwatchFromMetafield(jobData, optionName, optionValue);
      }

      // 3. Fallback: check if label has existing data-fallback-image
      if (!found && label.dataset.fallbackImage) {
        found = label.dataset.fallbackImage;
      }

      if (found) {
        if (found.startsWith('http://') || found.startsWith('https://') || found.startsWith('//') || found.startsWith('/cdn/')) {
          let img = label.querySelector('img');
          if (!img) {
            img = document.createElement('img');
            img.loading = 'lazy';
            img.alt = optionValue;
            img.width = 54;
            img.height = 54;
            label.innerHTML = '';
            label.appendChild(img);
          }
          img.src = found;
        } else if (found.startsWith('#') || found.startsWith('rgb')) {
          label.style.backgroundColor = found;
        }
      }
    });

    // Skin Color Swatches
    const skinLabels = root.querySelectorAll('.custom-skin-swatch-label, .pfh-skin-swatch-label, [data-pfh-skin-swatch]');
    skinLabels.forEach((label) => {
      const optionName = label.dataset.optionName || 'Skin Color';
      const optionValue = label.dataset.optionValue || label.getAttribute('title');
      if (!optionValue) return;

      let found = findSwatchFromMetafield(jobData, optionName, optionValue);
      const valLower = optionValue.trim().toLowerCase();

      // Check skin tone dictionary if not found in metafield
      if (!found && SKIN_TONES[valLower]) {
        found = SKIN_TONES[valLower];
      }

      if (found) {
        if (found.startsWith('#') || found.startsWith('rgb')) {
          label.style.backgroundColor = found;
        } else if (found.startsWith('http') || found.startsWith('//') || found.startsWith('/')) {
          label.style.backgroundImage = `url(${found})`;
          label.style.backgroundSize = 'cover';
        }
      }
    });
  }

  /**
   * Parse Variant Preview Images list (skin+style combo files) from JSON script tag
   */
  function getVariantPreviewFilesData() {
    const scriptTag = document.querySelector('script[data-pfh-variant-preview-files]');
    if (!scriptTag) return [];
    try {
      const raw = scriptTag.textContent.trim();
      if (!raw) return [];
      return JSON.parse(raw) || [];
    } catch (e) {
      console.warn('[Custom PDP] Error parsing variant preview files JSON:', e);
      return [];
    }
  }

  function normalizeToken(str) {
    return (str || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function getCheckedOptionValue(selector) {
    const input = document.querySelector(selector);
    return input ? input.value : null;
  }

  /**
   * Find a preview file whose (normalized) url or alt contains both the
   * selected skin token and the selected style token.
   */
  function findVariantPreviewMatch(previewFiles, skinValue, styleValue) {
    if (!previewFiles.length || !skinValue || !styleValue) return null;

    const skinToken = normalizeToken(skinValue);
    const styleToken = normalizeToken(styleValue);

    return (
      previewFiles.find((file) => {
        const cleanUrl = normalizeToken(file.url);
        const cleanAlt = normalizeToken(file.alt);
        return (
          (cleanUrl.includes(skinToken) && cleanUrl.includes(styleToken)) ||
          (cleanAlt && cleanAlt.includes(skinToken) && cleanAlt.includes(styleToken))
        );
      }) || null
    );
  }

  /**
   * The skin+style combo preview lives in its own dedicated gallery slide +
   * thumbnail (rendered hidden by product-media-gallery.liquid when the
   * variant_preview_images metafield is present). We only ever touch that
   * one slide's image/visibility - every other slide/thumbnail (the real
   * product photos) is left completely alone and keeps working natively.
   */
  function getVariantPreviewSlideEls() {
    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return null;

    const slide = mediaGallery.querySelector('.pfh-variant-preview-slide');
    const slideImg = slide && slide.querySelector('.pfh-variant-preview-image');
    if (!slide || !slideImg) return null;

    const thumb = mediaGallery.querySelector('.pfh-variant-preview-thumbnail');
    const thumbImg = thumb && thumb.querySelector('.pfh-variant-preview-thumbnail-image');

    return { mediaGallery, slide, slideImg, thumb, thumbImg };
  }

  /**
   * Populate/show the dedicated preview slide for the current skin+style
   * combo (hide it again if there's no matching file yet). Pass
   * `jumpToPreview: true` only in response to a direct skin/style pick, so
   * we don't yank the shopper away from a photo they navigated to manually.
   */
  function updateVariantPreview(jumpToPreview) {
    const previewFiles = getVariantPreviewFilesData();
    if (!previewFiles.length) return;

    const els = getVariantPreviewSlideEls();
    if (!els) return;

    const skinValue = getCheckedOptionValue('.custom-skin-swatch-input:checked, .pfh-skin-swatch-input:checked');
    const styleValue = getCheckedOptionValue('.custom-style-swatch-input:checked, .pfh-style-swatch-input:checked');
    const match = findVariantPreviewMatch(previewFiles, skinValue, styleValue);

    if (!match) {
      els.slide.hidden = true;
      if (els.thumb) els.thumb.hidden = true;
      return;
    }

    const alt = match.alt || `${skinValue} ${styleValue}`;
    els.slideImg.src = match.url;
    els.slideImg.alt = alt;
    if (els.thumbImg) {
      els.thumbImg.src = match.url;
      els.thumbImg.alt = alt;
    }

    const wasHidden = els.slide.hidden;
    els.slide.hidden = false;
    if (els.thumb) els.thumb.hidden = false;

    if (wasHidden) {
      // Slider component measured slide widths while this one was hidden
      // (clientWidth 0) - recompute now that it takes up real space.
      const viewer = els.mediaGallery.querySelector('[id^="GalleryViewer"]');
      const thumbSlider = els.mediaGallery.querySelector('[id^="GalleryThumbnails"]');
      if (viewer && typeof viewer.resetPages === 'function') viewer.resetPages();
      if (thumbSlider && typeof thumbSlider.resetPages === 'function') thumbSlider.resetPages();
    }

    if (jumpToPreview && typeof els.mediaGallery.setActiveMedia === 'function') {
      els.mediaGallery.setActiveMedia(els.slide.dataset.mediaId, false);
    }
  }

  /**
   * Desktop gallery main-image prev/next arrows.
   * Dawn's desktop "thumbnail" layout swaps images via the is-active class
   * (triggered by clicking a thumbnail), it doesn't scroll horizontally,
   * so the stock slider-button scrollTo() logic has nothing to scroll.
   * Wire the arrows to the same is-active switch the thumbnails use instead.
   */
  function initGalleryArrowNav() {
    const mediaGallery = document.querySelector('media-gallery');
    const viewer = mediaGallery && mediaGallery.querySelector('[id^="GalleryViewer"]');
    if (!mediaGallery || !viewer) return;

    const prevBtn = viewer.querySelector('.slider-buttons .slider-button--prev');
    const nextBtn = viewer.querySelector('.slider-buttons .slider-button--next');
    if (!prevBtn || !nextBtn || prevBtn.dataset.customNavBound) return;

    function goTo(direction) {
      const targets = Array.from(
        mediaGallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target]')
      ).map((el) => el.dataset.target);
      if (!targets.length) return;

      const activeMedia =
        viewer.querySelector('[data-media-id].is-active') || viewer.querySelector('[data-media-id]');
      if (!activeMedia) return;

      let index = targets.indexOf(activeMedia.dataset.mediaId);
      index += direction === 'next' ? 1 : -1;
      if (index < 0) index = targets.length - 1;
      if (index >= targets.length) index = 0;

      mediaGallery.setActiveMedia(targets[index], false);
    }

    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      goTo('prev');
    });
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      goTo('next');
    });
    prevBtn.dataset.customNavBound = 'true';
  }

  /**
   * Initial Setup and Event Observers
   */
  function init() {
    enhanceSwatches();
    updateVariantPreview(true);
    initGalleryArrowNav();

    // Instant swap + jump to the preview slide on skin/style selection,
    // before the AJAX variant fetch resolves
    document.addEventListener('change', (e) => {
      if (
        e.target.matches(
          '.custom-skin-swatch-input, .pfh-skin-swatch-input, .custom-style-swatch-input, .pfh-style-swatch-input'
        )
      ) {
        updateVariantPreview(true);
      }
    });

    // Re-run whenever section re-renders (Section Rendering API). Don't
    // force-jump here - this can fire for unrelated option changes (e.g.
    // size) and shouldn't yank the shopper away from a photo they're
    // currently looking at.
    if (window.pubsub) {
      pubsub.subscribe('optionValueSelectionChange', () => {
        setTimeout(() => {
          enhanceSwatches();
          updateVariantPreview(false);
        }, 100);
      });
    }

    // MutationObserver to detect DOM updates on variant-selects
    const target = document.querySelector('variant-selects, product-info');
    if (target && window.MutationObserver) {
      const observer = new MutationObserver(() => {
        enhanceSwatches();
        updateVariantPreview(false);
      });
      observer.observe(target, { childList: true, subtree: true });
    }

    // Initialize dynamic shipping dates + country selector
    updateShippingTimeline();
    initShippingCountryPicker();
    initShippingEtaModal();
  }

  /**
   * Calculate and display real-time shipping dates (arrives line + the
   * order-placed/ships/delivered dates inside the ETA timeline modal)
   */
  function updateShippingTimeline() {
    // Both the delivery card and the inline ETA timeline carry
    // [data-shipping-timeline], so refresh every one of them.
    document.querySelectorAll('[data-shipping-timeline]').forEach(updateShippingTimelineEl);
  }

  function updateShippingTimelineEl(timelineEl) {
    const shipMinDays = parseInt(timelineEl.dataset.shipMin, 10) || 3;
    const shipMaxDays = parseInt(timelineEl.dataset.shipMax, 10) || 5;
    const delMinDays = parseInt(timelineEl.dataset.delMin, 10) || 7;
    const delMaxDays = parseInt(timelineEl.dataset.delMax, 10) || 15;

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();

    function addDays(d, days) {
      const res = new Date(d);
      res.setDate(res.getDate() + days);
      return res;
    }

    function formatDateRange(startDate, endDate) {
      const sMonth = months[startDate.getMonth()];
      const eMonth = months[endDate.getMonth()];
      const sDay = startDate.getDate();
      const eDay = endDate.getDate();

      if (sMonth === eMonth) {
        return `${sMonth} ${sDay}–${eDay}`;
      }
      return `${sMonth} ${sDay}–${eMonth} ${eDay}`;
    }

    const todayFormatted = `${months[now.getMonth()]} ${now.getDate()}`;
    const shipRange = formatDateRange(addDays(now, shipMinDays), addDays(now, shipMaxDays));
    const delRange = formatDateRange(addDays(now, delMinDays), addDays(now, delMaxDays));

    const rangeHeader = timelineEl.querySelector('.custom-delivery-range');
    if (rangeHeader) rangeHeader.textContent = delRange;

    const placedEl = timelineEl.querySelector('[data-step-date="placed"]');
    if (placedEl) placedEl.textContent = todayFormatted;

    const shipEl = timelineEl.querySelector('[data-step-date="ships"]');
    if (shipEl) shipEl.textContent = shipRange;

    timelineEl.querySelectorAll('[data-step-date="delivered"], [data-step-date="delivered-modal"]').forEach((el) => {
      el.textContent = delRange;
    });
  }

  /**
   * ETA timeline modal (Order placed / Order ships / Delivered), opened by
   * clicking the "Arrives {date}" line.
   */
  function initShippingEtaModal() {
    const trigger = document.querySelector('[data-shipping-eta-trigger]');
    const root = trigger && trigger.closest('[data-shipping-timeline]');
    if (!root) return;

    const modal = root.querySelector('[data-shipping-eta-modal]');
    const closeEls = root.querySelectorAll('[data-shipping-eta-close]');
    if (!modal) return;

    function onKeydown(e) {
      if (e.key === 'Escape') closeModal();
    }

    function openModal() {
      modal.hidden = false;
      document.addEventListener('keydown', onKeydown);
    }

    function closeModal() {
      modal.hidden = true;
      document.removeEventListener('keydown', onKeydown);
    }

    trigger.addEventListener('click', openModal);
    closeEls.forEach((el) => el.addEventListener('click', closeModal));
  }

  /**
   * Domestic delivery country row + real country-picker modal.
   * The list is the store's actual markets (localization.available_countries,
   * rendered server-side); picking one submits the theme's own real
   * localization form (same one the header/footer country switcher uses),
   * which reloads the page with that market's currency applied. Since a
   * pick causes a real reload, `localization.country` is always the fresh
   * source of truth on every load - no local caching needed.
   */
  function initShippingCountryPicker() {
    const trigger = document.querySelector('[data-shipping-country-trigger]');
    const root = trigger && trigger.closest('[data-shipping-timeline]');
    if (!root) return;

    const modal = root.querySelector('[data-shipping-country-modal]');
    const closeEls = root.querySelectorAll('[data-shipping-country-close]');
    const searchInput = root.querySelector('[data-shipping-country-search]');
    const listEl = root.querySelector('[data-shipping-country-list]');
    const flagEl = root.querySelector('[data-shipping-flag]');
    const nameEl = root.querySelector('[data-shipping-country-name]');
    const modalFlagEl = root.querySelector('[data-shipping-modal-flag]');
    const modalNameEl = root.querySelector('[data-shipping-modal-current-name]');
    if (!trigger || !modal || !listEl) return;

    // Fill in every flag (row, modal header, each list item) from its ISO code.
    root.querySelectorAll('[data-shipping-flag-code]').forEach((el) => {
      el.textContent = isoToFlagEmoji(el.dataset.shippingFlagCode);
    });
    const currentCode = root.dataset.defaultCountryCode || 'US';
    const currentName = root.dataset.defaultCountryName || 'United States';
    const currentFlag = isoToFlagEmoji(currentCode);
    if (flagEl) flagEl.textContent = currentFlag;
    if (nameEl) nameEl.textContent = currentName;
    if (modalFlagEl) modalFlagEl.textContent = currentFlag;
    if (modalNameEl) modalNameEl.textContent = currentName;

    function submitRealCountryChange(code) {
      const input = document.querySelector('form input[name="country_code"]');
      const form = input && input.closest('form');
      if (!form) return false;
      input.value = code;
      form.submit();
      return true;
    }

    function onKeydown(e) {
      if (e.key === 'Escape') closeModal();
    }

    function openModal() {
      modal.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      if (searchInput) {
        searchInput.value = '';
        listEl.querySelectorAll('li').forEach((li) => (li.hidden = false));
        searchInput.focus();
      }
      document.addEventListener('keydown', onKeydown);
    }

    function closeModal() {
      modal.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onKeydown);
    }

    trigger.addEventListener('click', openModal);
    closeEls.forEach((el) => el.addEventListener('click', closeModal));

    listEl.querySelectorAll('[data-country-code]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!submitRealCountryChange(btn.dataset.countryCode)) closeModal();
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const term = searchInput.value.trim().toLowerCase();
        listEl.querySelectorAll('li').forEach((li) => {
          const btn = li.querySelector('[data-country-name]');
          const name = (btn && btn.dataset.countryName) || '';
          li.hidden = term.length > 0 && name.toLowerCase().indexOf(term) === -1;
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
